import { faChevronDown, faFileExport } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { BulkOpDef, ColumnToLabelMapping, RowData, Schema } from "src/components/ReportBuilder/ReportBuilder.io"
import { dayjsFormatOr, dayjsOr } from "src/helpers/formatDate"
import { assertTruthy, diacritishToAscii, EscapedRegExp, forceCheckedIndexedAccess, nonThrowingExhaustiveCaseGuard, parseIntOr, requireNonNull, sortBy, sortByAccentAwareCaseInsenstive, sortByDayJS, sortByMany, unreachable, vReqT } from "src/helpers/utils"
import { ColDef, downloadAsXlsxFactory, freshSortState } from "src/modules/TableUtils"
import { computed, defineComponent, ref, render, shallowRef } from "vue"
import { Btn2 } from "../UserInterface/Btn2"
import { FormKit } from "@formkit/vue"
import { Client } from "src/store/Client"
import { FkDynamicValidation } from "src/helpers/FkUtils"
import { BasicAutoTable2, typedBasicAutoTable2Props } from "src/modules/BasicAutoTable"
import { User } from "src/store/User"
import { MutUiSelection } from "../GameScheduler/calendar/PracticeSchedulerActions"
import { buildLegacyLink } from "src/boot/auth"
import { SoccerBall } from "../SVGs"
import { localDayJS2ExcelJS } from "src/modules/XlsxUtils"

export const QueryResultTable = defineComponent({
  props: {
    schema: vReqT<Schema>(),
    bulkOpsStore: vReqT<BulkOpsStore>(),
    data: vReqT<RowData[]>(),
    labels: vReqT<ColumnToLabelMapping>(),
    defaultInitialSort: vReqT<null | {colName: string, dir: "asc" | "desc"}[]>()
  },
  setup(props) {
    /**
     * if true, don't display columns C[n] where every row contains a `columnEmpty` value
     */
    const hideTotallyEmptyColumns = ref(true);
    const isColumnEmpty = (v: any) => v === "" || v === null || v === undefined

    // worst case O(rows * cols), this can be costly for large result sets
    // Guestimated P80-ish result sizes are 10K rows and 20 cols
    const totallyEmptyColIDs = computed(() => {
      return new Set<string>(
        Object
          .keys(props.labels)
          .filter((colID:string) => {
            // find first row having some non-`columnEmpty` value
            const hasSomeNonEmptyRow = props.data.some(row => !isColumnEmpty(row[colID]))
            // we are totally empty if no such row exists
            const isTotallyEmpty = !hasSomeNonEmptyRow
            return isTotallyEmpty
          })
      );
    })

    /**
     * note that a given row may not have all of the properties expected, because some were not relevant.
     * Particularly in the case of some selectable that expands to multiple columns, but some of those columns
     * are not relevant for the given row, that row may omit those columns.
     */

    const sortedLabelDefs = computed(() => {
      // TODO: -1 as "start new group" doesn't really work, we need to know which -1's form a group ... and that information is gone
      let lastColGroup = Math.max(...Object.values(props.labels).map(v => parseIntOr(v.columnGroup, -1)));
      const result = Object
        .entries(props.labels)
        .sort(
          sortByMany(
            sortBy(_ => _[1].columnGroup /*not strict*/ == -1 ? (lastColGroup++) : _[1].columnGroup), // TODO: fix unstable sort function, objs will potentially map to different lastColGroup every time
            sortBy(_ => _[1].columnGroupPosition),
          )
        )
      return result
    })

    const colDefs = computed<ColDef<RowData, string>[]>(() => {
      const seenColIDs = new Set<string>()
      const vs = sortedLabelDefs
        .value
        .filter(([colID]) => {
          return hideTotallyEmptyColumns.value
            ? !totallyEmptyColIDs.value.has(colID) // we are hiding totally empty columns, retain those columns that are NOT totally empty
            : true // not hiding totally empty columns, always retain every column
        })
        .map(([colID, info], i) : ColDef<RowData, string> => {
          assertTruthy(!seenColIDs.has(colID), `non-unique colID ${colID}`)
          seenColIDs.add(colID)

          const sorter : undefined | ((l: RowData, r: RowData) => number) = (() => {
            switch (info.colDataType) {
              case "date":
                return sortByDayJS(row => row[colID])
              case "datetime":
                return sortByDayJS(row => row[colID])
              case "string":
                return sortByAccentAwareCaseInsenstive(row => row[colID])
              case "money":
                // fallthrough
              case "bit-bool":
                // fallthrough
              case "number":
                return sortBy(row => row[colID])
              case "hyperlink":
                return undefined;
              case "html":
                return undefined
              default:
                nonThrowingExhaustiveCaseGuard(info.colDataType)
                console.warn(`unsupported colDataType '${info.colDataType}' on column '${colID}'`)
                return sortBy(row => row[colID])
            }
          })()

          return {
            id: colID,
            // we should always have a label for the name, but fallback just in case to "just" `field.name`
            label: info.label,
            labelHTML: () => {
              return <span
                // debug helper
                onClick={() => User.isInleague ? console.log(info) : null}
              >
                {info.label}
              </span>
            },
            headerClass: "p-1 border",
            html: row => {
              switch (info.colDataType) {
                case "date":
                  return dayjsFormatOr(row[colID], "MMM/DD/YYYY")
                case "datetime":
                  return dayjsFormatOr(row[colID], "MMM/DD/YYYY h:mm a")
                case "string":
                  return row[colID]
                case "money":
                  return row[colID] === "" ? "" : Number(row[colID]).toFixed(2)
                case "bit-bool":
                  return row[colID] /*weakEq*/ == 1 ? "Yes" : row[colID] /*weakEq*/ == 0 ? "No" : ""
                case "number":
                    return row[colID]
                case "hyperlink": {
                  const hl = getHyperlink(row, colID)
                  if (!hl) {
                    return null
                  }
                  if (hl.legacy) {
                    return <a class="il-link" href={buildLegacyLink(Client.value.instanceConfig.appdomain, hl.url, "")} target="_blank">{hl.label}</a>
                  }
                  else {
                    return <a class="il-link" href={hl.url} target="_blank">{hl.label}</a>
                  }
                }
                case "html":
                  return <div v-html={row[colID]}/>
                default:
                  nonThrowingExhaustiveCaseGuard(info.colDataType)
                  console.warn(`unsupported colDataType '${info.colDataType}' on column '${colID}'`)
                  return row[colID]
              }
            },
            xlsx: row => {
              switch (info.colDataType) {
                case "date":
                  return localDayJS2ExcelJS(dayjsOr(row[colID]), "date")
                case "datetime":
                  return localDayJS2ExcelJS(dayjsOr(row[colID]), "datetime")
                case "string":
                  return row[colID]
                case "money":
                  return row[colID]
                case "bit-bool":
                  return row[colID] /*weakEq*/ == 1 ? "Yes" : row[colID] /*weakEq*/ == 0 ? "No" : ""
                case "number":
                  return row[colID]
                case "hyperlink": {
                  const hl = getHyperlink(row, colID)
                  if (!hl) {
                    return null
                  }
                  return hl.label
                }
                case "html":
                  return getTextContentFromHTML(row[colID])
                default:
                  nonThrowingExhaustiveCaseGuard(info.colDataType)
                  console.warn(`unsupported colDataType '${info.colDataType}' on column '${colID}'`)
                  return row[colID]
              }
            },
            sort: sorter
          }
        });

      if (props.bulkOpsStore.showBulkOps) {
        vs.unshift({
          id: "__uiCheckAllCol__",
          label: "",
          headerClass: "p-1 border",
          labelHTML: () => {
            // functional component always re-renders, which we want here to always get the right "is everything checked" state
            return <Elem/>

            function Elem() {
              const checked = selectedRows.value.size > 0 && selectedRows.value.size === filteredRows.value.length
              return <div class="pl-1">
                <input
                  type="checkbox"
                  checked={checked}
                  onInput={() => {
                    if (selectedRows.value.size === filteredRows.value.length) {
                      // clear everything
                      selectedRows.value.clear()
                    }
                    else {
                      // add everything
                      selectedRows.value = new Map(
                        filteredRows.value.map(row => [pkExtractor.value(row), row])
                      )
                    }
                  }}
                />
              </div>
            }
          },
          html: row => {
            return <div class="pl-1">
              <input
                type="checkbox"
                checked={selectedRows.value.has(pkExtractor.value(row))}
                onInput={() => {
                  const pk = pkExtractor.value(row)
                  if (selectedRows.value.has(pk)) {
                    selectedRows.value.delete(pk)
                  }
                  else {
                    selectedRows.value.set(pk, row)
                  }
                }}
              />
            </div>
          },
          xlsx: "never"
        })
      }

      assertTruthy(new Set(vs.map(v => v.id)).size === vs.length, "column IDs must not contain duplicates");

      return vs;
    })

    const sortState = freshSortState(colDefs.value);
    if (props.defaultInitialSort) {
      const sanityCheckedAndRemapped = props
        .defaultInitialSort
        .filter(v => {
          if (!colDefs.value.find(colDef => colDef.id === v.colName)) {
            console.warn(`ignoring default sort colName '${v.colName}'`)
            return false
          }
          return true
        })
        .map(v => ({colID: v.colName, dir: v.dir}))

      sortState.reconfigure(sanityCheckedAndRemapped)
    }

    const downloadAsXlsx = async () => {
      const fileName = "ReportBuilder.xlsx" // ???
      const sortedRows = [...filteredRows.value].sort(sortState.asSorter())
      const runner = downloadAsXlsxFactory(colDefs.value)
      await runner(sortedRows, fileName)
    }

    const pkExtractor = computed<((row: RowData) => string)>(() => {
      const pk = props.schema.primaryKeyCol
      return (row: RowData) => requireNonNull(row[pk])
    })

    const bulkOpOptions = computed(() => {
      return props.schema.bulkOps.length === 0
        ? {disabled: true, options: []}
        : {disabled: false, options: props.schema.bulkOps.map(v => ({label: v.label, value: v.url}))}
    })

    const selectedRows = ref(new Map<string, RowData>())

    const HasNonZeroBulkSelectionValidator = FkDynamicValidation(() => {
      return selectedRows.value.size === 0
        ? {status: "invalid", msg: "No rows selected."}
        : {status: "valid"}
    })

    const filteredRows = ref(props.data)
    const filter = (() => {
      const searchText = ref("")
      const debounce_ms = 500
      let timeoutHandle : number = -1
      const busy = ref(false)

      // debounce is to not block the UI thread immediately, because it can be slow (on the order of a few seconds for ~50k records)
      // to filter through.
      const enqueueFilterRun = (searchText: string) => {
        busy.value = true
        window.clearTimeout(timeoutHandle)
        timeoutHandle = window.setTimeout(() => {
          // slight perf consideration - don't need to search through totally empty columns
          const cols = colDefs.value.filter(def => !totallyEmptyColIDs.value.has(def.id))
          filteredRows.value = props.data.filter(makeColDefFilter(searchText, cols))
          selectedRows.value.clear()
          busy.value = false
        }, debounce_ms)
      }

      return {
        get busy() { return busy.value },
        searchText,
        enqueueFilterRun
      }
    })()

    const tableProps = computed(() => typedBasicAutoTable2Props({
      colDefs: colDefs.value,
      rows: filteredRows.value,
      paginationOptions: [25, 50, 100, 200],
      sortState: sortState,
      rowKey: (e,i) => pkExtractor.value(e),
      rowAttrs: e => {
        const k = pkExtractor.value(e)
        return {
          "data-test": k || undefined,
        }
      },
      noData: () => <div class="p-2">No results.</div>,
      showSortOrder: true,
    }))

    const bulkOpKeysForForm = computed<string>(() => {
      const keyExtractor = props.bulkOpsStore.selection?.keyExtractor
      if (!keyExtractor) {
        return ""
      }
      else {
        // all keys of selected rows (note that the keys are potentially different from each row's PK)
        const keys = [...selectedRows.value.values()].map(keyExtractor)
        // because bulkOp keys are not necessarily a PK for each row, they are not guaranteed to be unique across the entire result set, so dedupe them
        return [...new Set(keys)].join(",")
      }
    })

    return () => {
      if (props.data.length === 0) {
        return <div class="w-full bg-white rounded-md shadow-md p-6 border border-slate-100 flex items-center justify-center">
          No results matching this query
        </div>
      }
      return (
        <div class="w-full bg-white rounded-md shadow-md p-2 border border-slate-100" style="--fk-margin-outer:none;">
          <div class="flex mb-2 gap-2 items-start">
            <div class={`p-1 ${props.bulkOpsStore.showBulkOps ? "outline outline-1 outline-gray-300 rounded-md" : ""}`}>
              {props.schema.bulkOps.length > 0
                ? <Btn2 class="px-1 text-sm flex items-center gap-1" onClick={() => { props.bulkOpsStore.toggleVisiblity(props.schema) }}>
                  <FontAwesomeIcon icon={faChevronDown} class={`${props.bulkOpsStore.showBulkOps ? '' : '-rotate-90 transform'} transition-transform duration-100`}/>
                  <span>Bulk Ops</span>
                </Btn2>
                : null}
              {props.bulkOpsStore.showBulkOps
                ? <FormKit
                  actions={false}
                  type="form"
                  method="POST"
                  action={
                    // Will be garbage if there is no `bulkOpsStore.selection`, but form should not be submittable in that case
                    // TODO: we need to know if it is a "legacy" or "modern" url, and adjust.
                    // Legacy ones do a post to backend and then re-routed as appropriate.
                    // "Modern" needs to ... we could bounce to server and back via this post ...
                    // or just store some stuff locally and push a new route.
                    // At this time this only supports legacy URLs.
                    buildLegacyLink(Client.value.instanceConfig.appdomain, props.bulkOpsStore.selection?.opDef.url ?? "", "")
                  }
                  target="_blank"
                >
                  <input type="hidden" name="queryType" value={props.bulkOpsStore.selection?.opDef.keyType ?? ""}/>
                  <input type="hidden" name="queryList" value={bulkOpKeysForForm.value}/>
                  <label>
                    <div class="text-sm font-medium">Send Selected Results To</div>
                    <FormKit
                      type="select"
                      options={bulkOpOptions.value.options}
                      disabled={bulkOpOptions.value.disabled}
                      v-model={props.bulkOpsStore.selectedURL}
                      placeholder="Select an Option"
                      validation={[["required"]]}
                    />
                  </label>
                  <HasNonZeroBulkSelectionValidator class="my-1"/>
                  <Btn2 class="px-2 py-1" type="submit">Send</Btn2>
                </FormKit>
                : null}
            </div>
            <label class="flex items-center gap-1 px-1 border rounded-md">
              <input type="checkbox" v-model={hideTotallyEmptyColumns.value}/>
              <span>Hide empty columns <span class="text-sm">({totallyEmptyColIDs.value.size})</span></span>
            </label>
            <label class="px-1 pb-1 border rounded-md">
              {filter.busy
                ? <span class="flex items-center gap-1"><SoccerBall size="1em"/>Filtering...</span>
                : <span>Filter</span>}
              <FormKit
                type="text"
                v-model={filter.searchText.value}
                onInput={(v: unknown) => {
                  if (typeof v !== "string") {
                    unreachable()
                  }
                  if (v === filter.searchText.value) {
                    return
                  }
                  filter.searchText.value = v
                  filter.enqueueFilterRun(v)
                }}
              />
            </label>
            <div class="ml-auto">
              <Btn2 onClick={downloadAsXlsx} class="px-2 py-1">
                <FontAwesomeIcon icon={faFileExport}/>
                <span class="ml-1 text-xs">As Excel</span>
              </Btn2>
            </div>
          </div>
          <div class="w-full overflow-x-auto">
            <BasicAutoTable2 {...tableProps.value}/>
          </div>
        </div>
      )
    }
  }
})

export type BulkOpsStore = ReturnType<typeof BulkOpsStore>
export function BulkOpsStore() {
  const nilSelection = () => MutUiSelection<string, BulkOpDef | null>(() => null, "")

  const showBulkOps = ref(false)
  const opts = shallowRef(nilSelection())
  const selectedURL = ref("")
  const keyExtractor = computed<((_: RowData) => string) | null>(() =>{
    const k = opts.value.selectedObj?.keyCol
    return k
      ? row => requireNonNull(row[k])
      : null
  })

  const reset = () => {
    opts.value = nilSelection()
    selectedURL.value = ""
  }

  return {
    get showBulkOps() { return showBulkOps.value },
    toggleVisiblity(schema: Schema) {
      if (showBulkOps.value) {
        showBulkOps.value = false;
      }
      else {
        selectedURL.value = schema.bulkOps.find(v => v.url === selectedURL.value)?.url // try to reuse older, nice if it has remained valid
          || forceCheckedIndexedAccess(schema.bulkOps, 0)?.url // fallback to first available
          || "" // no good option

        opts.value = MutUiSelection({
          get selectedKey() { return selectedURL.value },
          set selectedKey(v) { selectedURL.value = v },
          get selectedObj() {
            return schema.bulkOps.find(v => v.url === selectedURL.value) || null
          }
        });

        showBulkOps.value = true
      }
    },
    get selection() {
      if (!opts.value.selectedObj) {
        return null
      }
      else {
        return {
          opDef: opts.value.selectedObj,
          // if we have a selected opDef, we MUST have a keyExtractor
          keyExtractor: requireNonNull(keyExtractor.value)
        }
      }
    },
    get selectedURL() { return selectedURL.value },
    set selectedURL(v) { selectedURL.value = v },
    get keyExtractor() { return keyExtractor.value },
    reset,
  }
}

type Hyperlink = {
  legacy?: boolean,
  url: string,
  label: string,
}

function getHyperlink(row: RowData, key: string) : null | Hyperlink {
  const v = row[key]
  if (!v || typeof v !== "object") {
    return null
  }
  else if ("url" in v && "label" in v && typeof v.url === "string" && typeof v.label === "string") {
    return v;
  }
  else {
    return null
  }
}

const makeColDefFilter = (searchText: string, colDefs: ColDef<any, any>[]) : ((row: RowData) => boolean) => {
  if (searchText.trim() === "") {
    return () => true
  }

  const p = EscapedRegExp(diacritishToAscii(searchText.trim()), "i")
  const domContainer = document.createElement("div")

  const renderFuncs = colDefs
    .map(def => {
      if (typeof def.html !== "function") {
        console.warn(`def '${def.id}' in filterFunc was not a function`)
        return null
      }
      return def.html
    })
    .filter(v => v !== null)

  // for each row:
  //  - render each column to a memory-only DOM node and pull out its `textContent`
  //    (does `innerText` on a memory-only DOM node trigger a reflow? `textContent` shouldn't ever trigger a reflow, connected to Doc root or not...)
  //  - join all column text per row into one string
  //  - run regex against that string
  return (row: RowData) => {
    const rowText = renderFuncs
      .map((renderFunc) : string => {
        render(<>{renderFunc(row)}</>, domContainer)
        return diacritishToAscii(domContainer.textContent ?? "")
      })
      .join(" ")

    return p.test(rowText)
  }
}

const getTextContentFromHTML = (() => {
  const domContainer = document.createElement("div")
  return (html: string) : string => {
    // try to deal with newlines;
    // Note that elements like <div> and <br/> won't render newlines if they are not attached to the DOM.
    // If the HTML is simple with just `text<br/>text<br/>...` then this is fine,
    // otherwise, we could maybe do better, but complex layout results won't really be great in an excel cell.
    render(<div v-html={html.replace(/<[bh]r\/?>/ig, ";")}/>, domContainer)
    return domContainer.textContent || ""
  }
})()
